import {
    Grid,
    Box,
    Typography,
    Stack,
    FormHelperText,
    Alert,
    Button,
} from "@mui/material";
import StickyContent from "../../atoms/StickyContent/StickyContent";
import { useTranslation } from "react-i18next";
import FieldPaper from "../FieldPaper/FieldPaper";
import { useForm, Controller, FormProvider } from "react-hook-form";
import SubjectDids from "./fields/SubjectDids/SubjectDids.component";
import IssueAt from "./fields/IssueAt.component";
import InfoBlock from "../../atoms/InfoBlock/InfoBlock";
import Indicators from "./fields/Indicators/Indicators.component";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateValidatedQuestionFormSchema } from "./schema/CreateValidatedQuestionForm.schema";
import {
    GetIssuedVerifiableQueryCredentialsDocument,
    Status,
    useCheckActionResultQuery,
    useStartIssueCredentialsForIndicatorsMutation,
} from "@/src/graphql/generated/api/graphql";
import { useEffect, useMemo, useState } from "react";
import { type ExchangeProfileType } from "../../templates/CreateValidatedQuestion/schema/ExchangeProfile.schema";
import { CircularProgress } from "@mui/material";
import useGetFailuresForSubjectDids from "@/src/lib/useGetFailuresForSubjectDids/useGetFailuresForSubjectDids";
import { InvalidSubjectDidError } from "../InvalidSubjectDidError/InvalidSubjectDidError";
import { type z } from "zod";
import useGetIndicators from "@/src/lib/useGetIndicators/useGetIndicators";
import RevokeAt from "./fields/RevokeAt.component";
import LoadingDialog from "@/src/components/organisms/LoadingDialog/LoadingDialog";
import CredentialResultsDialog from "@/src/components/organisms/CredentialResultsDialog/CredentialResultsDialog";
import {
    CreateValidatedQuestionResultSchema,
    type CreateValidatedQuestionResultType,
} from "./schema/CreateValidatedQuestionResult.schema";
import { useNavigate } from "@tanstack/react-router";

export interface CreateValidatedQuestionFormProps {
    version: string;
    selectedIndicators: string[];
    exchangeProfile?: ExchangeProfileType;
}

type DialogType = "results" | "loading" | null;

type CreateValidatedQuestionFormSchemaType = z.infer<
    typeof CreateValidatedQuestionFormSchema
>;

export const CreateValidatedQuestionForm = ({
    version,
    exchangeProfile,
    selectedIndicators,
}: CreateValidatedQuestionFormProps) => {
    const [startIssueCredentialsForIndicatorsMutation] =
        useStartIssueCredentialsForIndicatorsMutation();
    const navigate = useNavigate();

    const [actionId, setActionId] = useState<string | null>(null);
    const [credentialResults, setCredentialResults] =
        useState<CreateValidatedQuestionResultType | null>(null);

    const [dialog, setDialog] = useState<DialogType>(null);

    const { data: pollData } = useCheckActionResultQuery({
        variables: { id: actionId! },
        skip: !actionId,
        pollInterval: 1000,
    });

    useMemo(() => {
        if (!pollData) return;

        if (
            pollData.checkAsyncAction.status === Status.Completed ||
            pollData.checkAsyncAction.status === Status.NoLongerAvailable
        ) {
            setActionId(null);
            if (pollData.checkAsyncAction.data) {
                const data = JSON.parse(pollData.checkAsyncAction.data);
                const parsedData =
                    CreateValidatedQuestionResultSchema.parse(data);

                setCredentialResults(parsedData);
            }
            setDialog("results");
        }
    }, [pollData]);

    const { t } = useTranslation();

    const [confirmed, setConfirmed] = useState<string[]>([]);

    const { indicators } = useGetIndicators(version, exchangeProfile?.title);

    const form = useForm<CreateValidatedQuestionFormSchemaType>({
        resolver: zodResolver(CreateValidatedQuestionFormSchema) as any,
        defaultValues: {
            subjectDids: [],
            indicators: indicators,
            issueAt: Date.now().toString(),
        } satisfies CreateValidatedQuestionFormSchemaType,
    });

    const formSubjectDids = form.watch("subjectDids");

    // Merge the indicators in the form with the new selected indicators, dropping previously configured indicators
    // when they become unselected. This way, if a user unselects and then re-selects the indicator, he/she will have
    // to reconfigure the parameter consciously.
    useMemo(() => {
        const currentlySelectedIndicators = indicators.filter(
            (formIndicator) => {
                return selectedIndicators.some(
                    (selectedIndicator) =>
                        selectedIndicator === formIndicator.path
                );
            }
        );

        form.setValue("indicators", [...currentlySelectedIndicators], {
            shouldValidate: true,
        });
    }, [selectedIndicators, form.setValue]);

    // Remove indicators that are no longer selected from the list of confirmed indicators
    useEffect(() => {
        setConfirmed((prev) => {
            return prev.filter((confirmed) => {
                return selectedIndicators.some(
                    (selectedIndicator) => selectedIndicator === confirmed
                );
            });
        });
    }, [selectedIndicators]);

    const minOneIndicatorSelected = selectedIndicators.length > 0;
    const allIndicatorsConfirmed =
        confirmed.length >= selectedIndicators.length;
    const formIsValid =
        form.formState.isValid &&
        minOneIndicatorSelected &&
        allIndicatorsConfirmed;

    const selectedFormIndicators = indicators.filter((indicator) => {
        return selectedIndicators.some(
            (selectedIndicator) => selectedIndicator === indicator.path
        );
    });

    const {
        loading: validationLoading,
        hasFailures,
        uniqueSubjectDidCount,
        conflictingCredentials,
        missingHealthcareOfficeIris,
    } = useGetFailuresForSubjectDids({
        formSubjectDids,
        selectedIndicators: selectedFormIndicators,
        version,
        exchangeProfile: exchangeProfile!,
    });

    const handleSubmit = form.handleSubmit(async (data) => {
        const response = await startIssueCredentialsForIndicatorsMutation({
            variables: {
                gitRemoteInput: {
                    repositoryUrl: exchangeProfile!.source,
                    branchOrTag: version,
                },
                credentialInput: {
                    indicators: data.indicators,
                    issueAt: data.issueAt,
                    revokeAt: data.revokeAt,
                },
                subjectIds: data.subjectDids.map(String),
            },
            refetchQueries: [GetIssuedVerifiableQueryCredentialsDocument],
        }).catch((err) => {
            console.error(err);
            return null;
        });

        if (response) {
            setDialog("loading");
            setActionId(
                response.data?.startIssueCredentialsForIndicatorsAction.id
            );
        }
    });

    const handleCloseDialog = () => {
        setDialog(null);
        void navigate({ to: "/" });
    };

    const loaderText = useMemo(() => {
        const results: any[] = pollData?.checkAsyncAction?.data
            ? JSON.parse(pollData.checkAsyncAction.data)
            : [];
        return `${results.length}/${selectedFormIndicators.length * formSubjectDids.length} ${t("loading-validated-questions")}`;
    }, [pollData, selectedFormIndicators.length, formSubjectDids.length, t]);

    return (
        <Grid size={{ xs: 12, md: 8 }}>
            <StickyContent>
                <FieldPaper
                    title={
                        <Typography variant="h6">
                            {t("validated-question-create.general-info")}
                        </Typography>
                    }
                >
                    <Box
                        sx={{
                            p: 0,
                            position: "relative",
                            isolation: "isolate",
                        }}
                    >
                        {form.formState.isSubmitting && (
                            <div
                                style={{
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    display: "grid",
                                    placeItems: "center",
                                    zIndex: "5",
                                }}
                            >
                                <CircularProgress />
                            </div>
                        )}
                        <FormProvider {...form}>
                            <form
                                style={{
                                    opacity: form.formState.isSubmitting
                                        ? 0.5
                                        : 1,
                                }}
                                onSubmit={handleSubmit}
                            >
                                <Stack gap={2}>
                                    <Controller
                                        name="subjectDids"
                                        control={form.control}
                                        render={({
                                            field: { onChange, onBlur, value },
                                        }) => (
                                            <SubjectDids
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                errors={{
                                                    message:
                                                        form.formState.errors
                                                            .subjectDids
                                                            ?.message,
                                                }}
                                                selectedSubjectDids={value.map(
                                                    (id) => String(id)
                                                )}
                                            />
                                        )}
                                    />
                                    {hasFailures ? (
                                        <FormHelperText error>
                                            {t(
                                                "validated-question-create.indicators.errors.subjectDidFailure",
                                                {
                                                    count: uniqueSubjectDidCount,
                                                }
                                            )}
                                        </FormHelperText>
                                    ) : null}

                                    <InvalidSubjectDidError
                                        loading={validationLoading}
                                        conflictingSubjectDidFailures={
                                            conflictingCredentials
                                        }
                                        missingHealthcareOfficeIriFailures={
                                            missingHealthcareOfficeIris
                                        }
                                    />

                                    <Controller
                                        name="issueAt"
                                        control={form.control}
                                        render={({ field: { onChange } }) => (
                                            <IssueAt
                                                onChange={onChange}
                                                errors={form.formState.errors}
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="revokeAt"
                                        control={form.control}
                                        render={({ field: { onChange } }) => (
                                            <RevokeAt
                                                onChange={onChange}
                                                errors={form.formState.errors}
                                            />
                                        )}
                                    />

                                    <InfoBlock
                                        label={t(
                                            "validated-question-create.exchangeProfile.title"
                                        )}
                                        value={
                                            exchangeProfile?.title ||
                                            t(
                                                "validated-question-create.exchangeProfile.empty-state"
                                            )
                                        }
                                        labelWidth="150px"
                                    />

                                    <InfoBlock
                                        label={t(
                                            "validated-question-create.version.title"
                                        )}
                                        value={
                                            version ||
                                            t(
                                                "validated-question-create.version.empty-state-form"
                                            )
                                        }
                                        labelWidth="150px"
                                    />
                                    {selectedFormIndicators.length > 0 ? (
                                        <Indicators
                                            indicators={selectedFormIndicators}
                                            exchangeProfile={exchangeProfile}
                                            version={version}
                                            confirmed={confirmed}
                                            setConfirmed={setConfirmed}
                                        />
                                    ) : (
                                        <InfoBlock
                                            label={t(
                                                "validated-question-create.indicators.title"
                                            )}
                                            value={t(
                                                "validated-question-create.indicators.none-selected"
                                            )}
                                            labelWidth="150px"
                                        />
                                    )}

                                    {!selectedIndicators &&
                                    form.formState.isSubmitted ? (
                                        <FormHelperText error>
                                            {t(
                                                "validated-question-create.indicators.errors.required"
                                            )}
                                        </FormHelperText>
                                    ) : null}

                                    {form.formState.isSubmitSuccessful &&
                                    Boolean(credentialResults) ? (
                                        <Alert severity="success">
                                            {t(
                                                "validated-question-create.success"
                                            )}
                                        </Alert>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            disabled={
                                                !formIsValid || hasFailures
                                            }
                                            loading={
                                                form.formState.isSubmitting ||
                                                validationLoading
                                            }
                                        >
                                            {t(
                                                "validated-question-create.submit"
                                            )}
                                        </Button>
                                    )}
                                </Stack>
                            </form>
                        </FormProvider>

                        {dialog === "loading" ? (
                            <LoadingDialog
                                open={true}
                                onClose={handleCloseDialog}
                                title={t("loading-validating-questions")}
                                loaderText={loaderText}
                            />
                        ) : null}

                        {dialog === "results" ? (
                            <CredentialResultsDialog
                                onClose={handleCloseDialog}
                                open={true}
                                results={credentialResults || undefined}
                            />
                        ) : null}
                    </Box>
                </FieldPaper>
            </StickyContent>
        </Grid>
    );
};

export default CreateValidatedQuestionForm;
